/**
 * Analytics Service
 * Handles database operations for user analytics and tracking
 * Updates: category_visit_analytics, search_history, recently_visited_categories
 */

import { supabase } from '@/lib/supabase';

// Types for analytics tracking
export interface CategoryVisitAnalytic {
  id?: string;
  user_id?: string;
  category_id?: string;
  subcategory_id?: string;
  product_category_id?: string;
  visit_type: 'category' | 'subcategory' | 'product_category';
  visit_count: number;
  date_recorded: string;
  source?: 'search' | 'navigation' | 'recommendation' | 'direct';
  session_id?: string;
  device_type?: 'mobile' | 'tablet' | 'desktop';
  time_spent?: number; // in seconds
  created_at?: string;
  updated_at?: string;
}

export interface SearchHistoryEntry {
  id?: string;
  user_id: string;
  query: string;
  results_count: number;
  filters?: Record<string, any>;
  category_clicked?: string;
  result_clicked?: boolean;
  search_source?: 'homepage' | 'header' | 'category_page' | 'product_page';
  created_at?: string;
  updated_at?: string;
}

export interface RecentlyVisitedCategory {
  id?: string;
  user_id: string;
  category_id?: string;
  subcategory_id?: string;
  product_category_id?: string;
  visit_type: 'category' | 'subcategory' | 'product_category';
  visit_count: number;
  first_visited_at: string;
  last_visited_at: string;
  source?: string;
  session_id?: string;
  device_type?: string;
  created_at?: string;
  updated_at?: string;
}

// Product tracking interfaces
export interface ProductVisitAnalytic {
  id?: string;
  user_id?: string;
  product_id: string;
  supplier_id?: string;
  category_id?: string;
  subcategory_id?: string;
  product_category_id?: string;
  visit_count: number;
  date_recorded: string;
  source?: 'search' | 'category' | 'recommendation' | 'direct' | 'rfq' | 'contact';
  session_id?: string;
  device_type?: 'mobile' | 'tablet' | 'desktop';
  time_spent?: number; // in seconds
  interaction_type?: 'view' | 'contact' | 'rfq' | 'share' | 'favorite';
  created_at?: string;
  updated_at?: string;
}

export interface RecentlyVisitedProduct {
  id?: string;
  user_id: string;
  product_id: string;
  supplier_id?: string;
  category_id?: string;
  subcategory_id?: string;
  product_category_id?: string;
  visit_count: number;
  first_visited_at: string;
  last_visited_at: string;
  source?: string;
  session_id?: string;
  device_type?: string;
  interaction_type?: string;
  created_at?: string;
  updated_at?: string;
}

export class AnalyticsService {
  
  /**
   * Record category visit
   */
  async recordCategoryVisit(params: {
    userId?: string;
    categoryId?: string;
    subcategoryId?: string;
    productCategoryId?: string;
    visitType: 'category' | 'subcategory' | 'product_category';
    source?: 'search' | 'navigation' | 'recommendation' | 'direct';
    sessionId?: string;
    deviceType?: 'mobile' | 'tablet' | 'desktop';
    timeSpent?: number;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const {
        userId,
        categoryId,
        subcategoryId,
        productCategoryId,
        visitType,
        source = 'direct',
        sessionId,
        deviceType = 'desktop',
        timeSpent
      } = params;

      const now = new Date().toISOString();
      const today = new Date().toISOString().split('T')[0];

      // Only update recently_visited_categories for user tracking
      // Skip category_visit_analytics as it has a different schema for aggregated data
      if (userId) {
        await this.updateRecentlyVisitedCategories({
          userId,
          categoryId,
          subcategoryId,
          productCategoryId,
          visitType,
          source,
          sessionId,
          deviceType
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error recording category visit:', error);
      
      // Better error handling to get more details
      let errorMessage = 'Unknown error';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        // Handle Supabase error objects
        if ('message' in error) {
          errorMessage = (error as any).message;
        } else if ('details' in error) {
          errorMessage = (error as any).details;
        } else {
          errorMessage = JSON.stringify(error);
        }
      }
      
      console.error('Detailed error message:', errorMessage);
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  }

  /**
   * Update category_visit_analytics table
   * Note: This function is currently disabled as the existing database schema
   * for category_visit_analytics uses aggregated columns (total_visits, unique_visitors, etc.)
   * rather than individual visit tracking columns (user_id, visit_count, etc.)
   */
  private async updateCategoryVisitAnalytics(params: {
    userId?: string;
    categoryId?: string;
    subcategoryId?: string;
    productCategoryId?: string;
    visitType: string;
    source?: string;
    sessionId?: string;
    deviceType?: string;
    timeSpent?: number;
    dateRecorded: string;
  }): Promise<void> {
    // Skipping category_visit_analytics updates as the existing schema
    // is designed for aggregated data, not individual visit tracking
    // Individual visits are tracked in recently_visited_categories instead
    return;
  }

  /**
   * Update recently_visited_categories table
   */
  private async updateRecentlyVisitedCategories(params: {
    userId: string;
    categoryId?: string;
    subcategoryId?: string;
    productCategoryId?: string;
    visitType: string;
    source?: string;
    sessionId?: string;
    deviceType?: string;
  }): Promise<void> {
    const {
      userId,
      categoryId,
      subcategoryId,
      productCategoryId,
      visitType,
      source,
      sessionId,
      deviceType
    } = params;

    console.log('Attempting to update recently_visited_categories with params:', {
      userId,
      categoryId,
      subcategoryId,
      productCategoryId,
      visitType,
      source,
      sessionId,
      deviceType
    });

    const now = new Date().toISOString();

    try {
      // Check if user has visited this category before
      const { data: existingVisit, error: selectError } = await supabase
        .from('recently_visited_categories')
        .select('id, visit_count, first_visited_at')
        .match({
          user_id: userId,
          category_id: categoryId || null,
          subcategory_id: subcategoryId || null,
          product_category_id: productCategoryId || null,
          visit_type: visitType
        })
        .single();

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error selecting existing visit:', selectError);
        throw selectError;
      }

      if (existingVisit) {
        console.log('Updating existing visit record:', existingVisit.id);
        // Update existing record with explicit updated_at
        const { error: updateError } = await supabase
          .from('recently_visited_categories')
          .update({
            visit_count: existingVisit.visit_count + 1,
            last_visited_at: now,
            source: source || 'direct',
            session_id: sessionId || null,
            device_type: deviceType || 'desktop',
            updated_at: now
          })
          .eq('id', existingVisit.id);

        if (updateError) {
          console.error('Error updating existing visit:', updateError);
          throw updateError;
        }
      } else {
        console.log('Inserting new visit record');
        // Insert new record with explicit timestamps
        const insertData = {
          user_id: userId,
          category_id: categoryId || null,
          subcategory_id: subcategoryId || null,
          product_category_id: productCategoryId || null,
          visit_type: visitType,
          visit_count: 1,
          first_visited_at: now,
          last_visited_at: now,
          source: source || 'direct',
          session_id: sessionId || null,
          device_type: deviceType || 'desktop',
          created_at: now,
          updated_at: now
        };

        console.log('Insert data:', insertData);

        const { error: insertError } = await supabase
          .from('recently_visited_categories')
          .insert(insertData);

        if (insertError) {
          console.error('Error inserting new visit:', insertError);
          throw insertError;
        }
      }

      console.log('Successfully updated recently_visited_categories');
    } catch (error) {
      console.error('Error in updateRecentlyVisitedCategories:', error);
      throw error;
    }
  }

  /**
   * Record search history
   */
  async recordSearch(params: {
    userId: string;
    query: string;
    resultsCount: number;
    filters?: Record<string, any>;
    categoryClicked?: string;
    resultClicked?: boolean;
    searchSource?: 'homepage' | 'header' | 'category_page' | 'product_page';
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const {
        userId,
        query,
        resultsCount,
        filters,
        categoryClicked,
        resultClicked,
        searchSource = 'homepage'
      } = params;

      const now = new Date().toISOString();

      const { error } = await supabase
        .from('search_history')
        .insert({
          user_id: userId,
          query: query.trim(),
          results_count: resultsCount,
          filters: filters || null,
          category_clicked: categoryClicked || null,
          result_clicked: resultClicked || false,
          search_source: searchSource,
          created_at: now,
          updated_at: now
        });

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error recording search:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get user's search history
   */
  async getUserSearchHistory(
    userId: string, 
    limit: number = 10
  ): Promise<SearchHistoryEntry[]> {
    try {
      const { data, error } = await supabase
        .from('search_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching search history:', error);
      return [];
    }
  }

  /**
   * Get user's recently visited categories
   */
  async getUserRecentlyVisitedCategories(
    userId: string,
    limit: number = 10
  ): Promise<RecentlyVisitedCategory[]> {
    try {
      const { data, error } = await supabase
        .from('recently_visited_categories')
        .select('*')
        .eq('user_id', userId)
        .order('last_visited_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching recently visited categories:', error);
      return [];
    }
  }

  /**
   * Record product visit analytics
   * Updates both product_visit_analytics and recently_visited_products tables
   */
  async recordProductVisit(params: {
    userId?: string;
    productId: string;
    supplierId?: string;
    categoryId?: string;
    subcategoryId?: string;
    productCategoryId?: string;
    source?: 'search' | 'category' | 'recommendation' | 'direct' | 'rfq' | 'contact';
    sessionId?: string;
    deviceType?: 'mobile' | 'tablet' | 'desktop';
    timeSpent?: number;
    interactionType?: 'view' | 'contact' | 'rfq' | 'share' | 'favorite';
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const {
        userId,
        productId,
        supplierId,
        categoryId,
        subcategoryId,
        productCategoryId,
        source = 'direct',
        sessionId,
        deviceType = 'desktop',
        timeSpent,
        interactionType = 'view'
      } = params;

      const now = new Date().toISOString();
      const today = new Date().toISOString().split('T')[0];

      // 1. Update product_visit_analytics (daily aggregated data)
      await this.updateProductVisitAnalytics({
        userId,
        productId,
        supplierId,
        categoryId,
        subcategoryId,
        productCategoryId,
        source,
        sessionId,
        deviceType,
        timeSpent,
        interactionType,
        dateRecorded: today
      });

      // 2. Update recently_visited_products (user's recent activity)
      if (userId) {
        await this.updateRecentlyVisitedProducts({
          userId,
          productId,
          supplierId,
          categoryId,
          subcategoryId,
          productCategoryId,
          source,
          sessionId,
          deviceType,
          interactionType
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error recording product visit:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Update product_visit_analytics table
   */
  private async updateProductVisitAnalytics(params: {
    userId?: string;
    productId: string;
    supplierId?: string;
    categoryId?: string;
    subcategoryId?: string;
    productCategoryId?: string;
    source?: string;
    sessionId?: string;
    deviceType?: string;
    timeSpent?: number;
    interactionType?: string;
    dateRecorded: string;
  }): Promise<void> {
    const {
      userId,
      productId,
      supplierId,
      categoryId,
      subcategoryId,
      productCategoryId,
      source,
      sessionId,
      deviceType,
      timeSpent,
      interactionType,
      dateRecorded
    } = params;

    // Check if record exists for today
    const { data: existingRecord, error: fetchError } = await supabase
      .from('product_visit_analytics')
      .select('*')
      .eq('product_id', productId)
      .eq('date_recorded', dateRecorded)
      .eq('user_id', userId || '')
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      throw fetchError;
    }

    const now = new Date().toISOString();

    if (existingRecord) {
      // Update existing record with explicit updated_at
      const { error: updateError } = await supabase
        .from('product_visit_analytics')
        .update({
          visit_count: existingRecord.visit_count + 1,
          time_spent: timeSpent ? (existingRecord.time_spent || 0) + timeSpent : existingRecord.time_spent,
          interaction_type: interactionType,
          session_id: sessionId,
          device_type: deviceType,
          updated_at: now
        })
        .eq('id', existingRecord.id);

      if (updateError) throw updateError;
    } else {
      // Insert new record with explicit timestamps
      const { error: insertError } = await supabase
        .from('product_visit_analytics')
        .insert({
          user_id: userId || null,
          product_id: productId,
          supplier_id: supplierId || null,
          category_id: categoryId || null,
          subcategory_id: subcategoryId || null,
          product_category_id: productCategoryId || null,
          visit_count: 1,
          date_recorded: dateRecorded,
          source: source || null,
          session_id: sessionId || null,
          device_type: deviceType || null,
          time_spent: timeSpent || null,
          interaction_type: interactionType || null,
          created_at: now,
          updated_at: now
        });

      if (insertError) throw insertError;
    }
  }

  /**
   * Update recently_visited_products table
   */
  private async updateRecentlyVisitedProducts(params: {
    userId: string;
    productId: string;
    supplierId?: string;
    categoryId?: string;
    subcategoryId?: string;
    productCategoryId?: string;
    source?: string;
    sessionId?: string;
    deviceType?: string;
    interactionType?: string;
  }): Promise<void> {
    const {
      userId,
      productId,
      supplierId,
      categoryId,
      subcategoryId,
      productCategoryId,
      source,
      sessionId,
      deviceType,
      interactionType
    } = params;

    // Check if record exists for this user and product
    const { data: existingRecord, error: fetchError } = await supabase
      .from('recently_visited_products')
      .select('*')
      .eq('user_id', userId)
      .eq('product_id', productId)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      throw fetchError;
    }

    const now = new Date().toISOString();

    if (existingRecord) {
      // Update existing record with explicit updated_at
      const { error: updateError } = await supabase
        .from('recently_visited_products')
        .update({
          visit_count: existingRecord.visit_count + 1,
          last_visited_at: now,
          source: source || existingRecord.source,
          session_id: sessionId || existingRecord.session_id,
          device_type: deviceType || existingRecord.device_type,
          interaction_type: interactionType || existingRecord.interaction_type,
          updated_at: now
        })
        .eq('id', existingRecord.id);

      if (updateError) throw updateError;
    } else {
      // Insert new record with explicit timestamps
      const { error: insertError } = await supabase
        .from('recently_visited_products')
        .insert({
          user_id: userId,
          product_id: productId,
          supplier_id: supplierId || null,
          category_id: categoryId || null,
          subcategory_id: subcategoryId || null,
          product_category_id: productCategoryId || null,
          visit_count: 1,
          first_visited_at: now,
          last_visited_at: now,
          source: source || null,
          session_id: sessionId || null,
          device_type: deviceType || null,
          interaction_type: interactionType || null,
          created_at: now,
          updated_at: now
        });

      if (insertError) throw insertError;
    }
  }

  /**
   * Get user's recently visited products
   */
  async getUserRecentlyVisitedProducts(
    userId: string,
    limit: number = 10
  ): Promise<RecentlyVisitedProduct[]> {
    try {
      const { data, error } = await supabase
        .from('recently_visited_products')
        .select('*')
        .eq('user_id', userId)
        .order('last_visited_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching recently visited products:', error);
      return [];
    }
  }

  /**
   * Get popular products based on visit analytics
   */
  async getPopularProducts(
    daysBack: number = 7,
    limit: number = 10
  ): Promise<any[]> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);
      const formattedStartDate = startDate.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('product_visit_analytics')
        .select(`
          product_id,
          supplier_id,
          category_id,
          subcategory_id,
          product_category_id,
          visit_count,
          interaction_type
        `)
        .gte('date_recorded', formattedStartDate)
        .order('visit_count', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Group by product_id and sum visit counts
      const productGroups: Record<string, any> = {};
      (data || []).forEach(item => {
        const productId = item.product_id;
        if (!productGroups[productId]) {
          productGroups[productId] = {
            product_id: productId,
            supplier_id: item.supplier_id,
            category_id: item.category_id,
            subcategory_id: item.subcategory_id,
            product_category_id: item.product_category_id,
            total_visits: 0
          };
        }
        productGroups[productId].total_visits += item.visit_count;
      });

      return Object.values(productGroups)
        .sort((a, b) => b.total_visits - a.total_visits)
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching popular products:', error);
      return [];
    }
  }

  /**
   * Get product analytics for admin dashboard
   */
  async getProductAnalytics(params: {
    startDate?: string;
    endDate?: string;
    productId?: string;
    supplierId?: string;
    categoryId?: string;
    interactionType?: string;
    limit?: number;
  } = {}): Promise<ProductVisitAnalytic[]> {
    try {
      const {
        startDate,
        endDate,
        productId,
        supplierId,
        categoryId,
        interactionType,
        limit = 100
      } = params;

      let query = supabase
        .from('product_visit_analytics')
        .select('*');

      if (startDate) {
        query = query.gte('date_recorded', startDate);
      }

      if (endDate) {
        query = query.lte('date_recorded', endDate);
      }

      if (productId) {
        query = query.eq('product_id', productId);
      }

      if (supplierId) {
        query = query.eq('supplier_id', supplierId);
      }

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      if (interactionType) {
        query = query.eq('interaction_type', interactionType);
      }

      const { data, error } = await query
        .order('date_recorded', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching product analytics:', error);
      return [];
    }
  }

  /**
   * Get category visit analytics for admin dashboard
   */
  async getCategoryAnalytics(params: {
    startDate?: string;
    endDate?: string;
    categoryId?: string;
    visitType?: string;
    limit?: number;
  } = {}): Promise<CategoryVisitAnalytic[]> {
    try {
      const {
        startDate,
        endDate,
        categoryId,
        visitType,
        limit = 100
      } = params;

      let query = supabase
        .from('category_visit_analytics')
        .select('*');

      if (startDate) {
        query = query.gte('date_recorded', startDate);
      }

      if (endDate) {
        query = query.lte('date_recorded', endDate);
      }

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data, error } = await query
        .order('date_recorded', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Transform the data to match CategoryVisitAnalytic interface
      // Map existing schema columns to expected interface
      const transformedData = (data || []).map(item => ({
        id: item.id,
        user_id: undefined, // Not tracked in aggregated table
        category_id: item.category_id,
        subcategory_id: item.subcategory_id,
        product_category_id: item.product_category_id,
        visit_count: item.total_visits || 0, // Map total_visits to visit_count
        date_recorded: item.date_recorded,
        source: undefined, // Not available in aggregated table
        session_id: undefined, // Not available in aggregated table
        device_type: undefined, // Not available in aggregated table
        time_spent: undefined, // Not available in aggregated table
        created_at: item.created_at,
        updated_at: item.updated_at,
        visit_type: (item.category_id ? 'category' : 
                    item.subcategory_id ? 'subcategory' : 
                    item.product_category_id ? 'product_category' : 'category') as 'category' | 'subcategory' | 'product_category'
      }));

      // Apply visitType filter after transformation if specified
      if (visitType) {
        return transformedData.filter(item => item.visit_type === visitType);
      }

      return transformedData;
    } catch (error) {
      console.error('Error fetching category analytics:', error);
      return [];
    }
  }

  /**
   * Get popular categories based on visit analytics
   */
  async getPopularCategories(
    daysBack: number = 7,
    limit: number = 10
  ): Promise<any[]> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);
      const formattedStartDate = startDate.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('category_visit_analytics')
        .select('category_id, subcategory_id, product_category_id, total_visits')
        .gte('date_recorded', formattedStartDate)
        .order('total_visits', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Transform the data to include visit_type based on which ID is present
      const transformedData = (data || []).map(item => ({
        ...item,
        visit_count: item.total_visits || 0, // Map total_visits to visit_count for consistency
        visit_type: item.category_id ? 'category' : 
                   item.subcategory_id ? 'subcategory' : 
                   item.product_category_id ? 'product_category' : 'category'
      }));

      return transformedData;
    } catch (error) {
      console.error('Error fetching popular categories:', error);
      return [];
    }
  }

  /**
   * Get search trends
   */
  async getSearchTrends(
    daysBack: number = 7,
    limit: number = 10
  ): Promise<any[]> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      const { data, error } = await supabase
        .from('search_history')
        .select('query, results_count, created_at')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Group by query to get trending searches
      const queryGroups: Record<string, number> = {};
      data?.forEach(search => {
        const query = search.query.toLowerCase().trim();
        queryGroups[query] = (queryGroups[query] || 0) + 1;
      });

      return Object.entries(queryGroups)
        .map(([query, count]) => ({ query, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching search trends:', error);
      return [];
    }
  }

  /**
   * Clean old analytics data
   */
  async cleanupOldData(daysToKeep: number = 90): Promise<{ success: boolean; error?: string }> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
      const cutoffDateString = cutoffDate.toISOString();

      // Clean up old category visit analytics
      const { error: analyticsError } = await supabase
        .from('category_visit_analytics')
        .delete()
        .lt('created_at', cutoffDateString);

      if (analyticsError) throw analyticsError;

      // Clean up old search history
      const { error: searchError } = await supabase
        .from('search_history')
        .delete()
        .lt('created_at', cutoffDateString);

      if (searchError) throw searchError;

      // Clean up old recently visited categories (keep last 30 days only)
      const recentCutoff = new Date();
      recentCutoff.setDate(recentCutoff.getDate() - 30);

      const { error: recentError } = await supabase
        .from('recently_visited_categories')
        .delete()
        .lt('last_visited_at', recentCutoff.toISOString());

      if (recentError) throw recentError;

      return { success: true };
    } catch (error) {
      console.error('Error cleaning up old data:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService(); 